import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { VProgressBar } from "@app/directives/_misc/v-progress-bar/v-progress-bar.component";
import { SortType } from "@enums/SortType";
import { TranslationService } from "@services/translation.service";
import { DrivingStyleMetric, DrivingStyleWeightingType, MetricMappingWeightingLevel } from "../driving-styles.enums";
import { IDrivingStyle, IDrivingStyleMetricMapping } from "../driving-styles.interfaces";
import { DrivingStylesService, IMetricMappingWeightingLevelDetail } from "../driving-styles.service";
import './driving-style-metric-editor.component.scss';

interface IDisplayMetricMapping {
    metric: DrivingStyleMetric;
    label: string;
    count: number;
    data: IDrivingStyleMetricMapping;
}

@Component({
    selector: 'driving-style-metric-editor',
    template: require('./driving-style-metric-editor.component.html')
})
export class DrivingStyleMetricEditorComponent implements OnInit, OnChanges {

    @Input() drivingStyle: IDrivingStyle;
    @Input() weightingType: DrivingStyleWeightingType;
    @Input() metricMapping: IDrivingStyleMetricMapping;
    @Output() metricMappingChange = new EventEmitter<IDrivingStyleMetricMapping>();
    @Output() alterativeMetricClick = new EventEmitter<DrivingStyleMetric>(); 

    metricMappingWeightingLevelDetails: IMetricMappingWeightingLevelDetail[];
    defaultMetricMappingWeightingLevelDetail: IMetricMappingWeightingLevelDetail;
    selectedWeightingLevelDetail: IMetricMappingWeightingLevelDetail;

    enableCustomWeighting: boolean;

    MetricMappingWeightingLevel = MetricMappingWeightingLevel;

    metricForm: FormGroup;

    highScoreIsBad: boolean;

    displayMetricMappings: IDisplayMetricMapping[];
    sliderValues: number[];
    SortType = SortType;

    tableColumns: IVTableColumn<IDisplayMetricMapping>[];
    tableRows: IVTableRow[];

    get virtualHighScoreValue(): AbstractControl {
        return this.metricForm?.get("VirtualHighScoreValue");
    }

    get upperRagBoundControl(): AbstractControl {
        return this.metricForm?.get("UpperRagBound");
    }

    get lowerRagBoundControl(): AbstractControl {
        return this.metricForm?.get("LowerRagBound");
    }

    get customWeightingControl(): AbstractControl {
        return this.metricForm?.get("CustomWeighting");
    }

    constructor(
        private readonly translationService: TranslationService,
        private readonly drivingStylesService: DrivingStylesService,
        private readonly formBuilder: FormBuilder,
        private readonly vTableService: VTableService
    ) {

    }

    ngOnInit(): void {
        this.prepare();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.prepare();
    }

    onWeightingToggleChanged(value: boolean): void {
        if (value && this.defaultMetricMappingWeightingLevelDetail) {
            this.metricMapping.Weighting = this.defaultMetricMappingWeightingLevelDetail.value;
            this.selectWeightingLevelDetail(this.metricMapping.Weighting);
        } else {
            this.metricMapping.Weighting = null;
        }

        this.emitChange();
    }

    onWeightingLevelDetailSelectChange(selectedWeightingLevelDetail: IMetricMappingWeightingLevelDetail): void {
        this.metricMapping.Weighting = +selectedWeightingLevelDetail.value;
        this.selectWeightingLevelDetail(this.metricMapping.Weighting);

        this.emitChange();
    }

    onCustomWeightingChanged(newCustomWeighting: number): void {
        this.metricMapping.Weighting = +newCustomWeighting;

        this.customWeightingControl.setValue(this.metricMapping.Weighting);
        this.customWeightingControl.updateValueAndValidity();
        this.drivingStylesService.updateCustomWeightingsError(this.drivingStyle);

        this.emitChange();
    }

    onHighScoreValueChanged(): void {
        this.virtualHighScoreValue.setValue(this.metricMapping.HighScoreValue);
        this.virtualHighScoreValue.updateValueAndValidity();

        this.emitChange();
    }

    onLowScoreValueChanged(): void {
        this.virtualHighScoreValue.updateValueAndValidity();
        this.emitChange();
    }

    onSliderValuesChanged(values: number[]): void {
        this.metricMapping.LowerRagBound = Math.round(values[0]);
        this.metricMapping.UpperRagBound = Math.round(values[1]);

        this.upperRagBoundControl.setValue(this.metricMapping.UpperRagBound);
        this.lowerRagBoundControl.setValue(this.metricMapping.LowerRagBound);

        this.upperRagBoundControl.updateValueAndValidity();
        this.lowerRagBoundControl.updateValueAndValidity();
    }

    updateDisplayMetricMappings(): void {
        this.displayMetricMappings = [];

        this.drivingStyle.DrivingStyleMetricMappings.forEach((metricMapping, index) => {

            const weightingCount = this.weightingType === DrivingStyleWeightingType.Simple
                ? +(metricMapping.metricMetadataItem?.ProportionOfTotalScore ?? 0).toFixed(2)
                : metricMapping.Weighting;

            const displayMetricMapping: IDisplayMetricMapping = {
                metric: metricMapping.DrivingStyleMetric,
                label: metricMapping.drivingStyleMetricName,
                count: weightingCount,
                data: metricMapping,
            };

            this.displayMetricMappings.push(displayMetricMapping);
        });

        this.createTableRows();
    }

    //--

    private prepare(): void {
        this.createTable();
        this.determineScoreDirection();
        this.updateMetricMappingWeightingLevelDetail();
        this.selectWeightingLevelDetail(+this.metricMapping.Weighting);
        this.updateSliderValues();

        this.buildForm();
        this.updateDisplayMetricMappings();
        this.drivingStylesService.updateCustomWeightingsError(this.drivingStyle);
        this.createTableRows();
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.displayMetricMappings, this.tableColumns);
    }

    private updateSliderValues(): void {
        this.sliderValues = [this.metricMapping.LowerRagBound, this.metricMapping.UpperRagBound];
    }

    private emitChange(): void {
        this.updateDisplayMetricMappings();

        if (this.metricForm.invalid) {
            return;
        }

        this.metricMappingChange.emit(this.metricMapping);
    }

    private updateMetricMappingWeightingLevelDetail(): void {
        this.metricMappingWeightingLevelDetails = this.drivingStylesService.getMetricMappingWeightingLevelDetails().filter(detail => !detail.hideFromDropdown);

        this.defaultMetricMappingWeightingLevelDetail = this.metricMappingWeightingLevelDetails.find(option => option.isDefault);
    }


    private selectWeightingLevelDetail(weighting: number): void {
        if (this.weightingType === DrivingStyleWeightingType.Custom) {
            return;
        }

        this.selectedWeightingLevelDetail = this.metricMappingWeightingLevelDetails.find(detail => detail.value === +weighting);

        if (!this.selectedWeightingLevelDetail) {
            return;
        }

        this.drivingStylesService.setWeightingLevelDetailOnMetric(this.selectedWeightingLevelDetail, this.metricMapping);
        this.updateDisplayMetricMappings();
    }

    private buildForm(): void {
        this.metricForm = this.formBuilder.group({
            VirtualHighScoreValue: [this.metricMapping.HighScoreValue, [Validators.required, Validators.min(0), Validators.max(100), this.validateScoreValue()]],
            UpperRagBound: [this.metricMapping.UpperRagBound, [Validators.required, Validators.min(0), Validators.max(100), this.validateRagBound()]],
            LowerRagBound: [this.metricMapping.LowerRagBound, [Validators.required, Validators.min(0), Validators.max(100), this.validateRagBound()]],
            CustomWeighting: [this.metricMapping.Weighting ?? 1, [Validators.min(1), Validators.max(100)]]
        });
    }

    private validateScoreValue(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            if (!this.metricForm || !this.metricMapping) {
                return null;
            }

            const HighScoreValue: number = this.metricMapping.HighScoreValue;
            const LowScoreValue: number = this.metricMapping.LowScoreValue;

            if (HighScoreValue <= LowScoreValue && !this.highScoreIsBad) {
                return { customMessage: this.translationService.t("DrivingStyleMetricEditor.BadScoreMustBeLowerValidationFail") }

            } else if (HighScoreValue >= LowScoreValue && this.highScoreIsBad) {
                return { customMessage: this.translationService.t("DrivingStyleMetricEditor.BadScoreMustBeHigherValidationFail") }
            }

            return null;
        }
    }

    private validateRagBound(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            if (!this.metricForm) {
                return null;
            }

            const upperRagBoundValue: number = this.upperRagBoundControl?.value;
            const lowerRagBoundValue: number = this.lowerRagBoundControl?.value;

            if (upperRagBoundValue <= lowerRagBoundValue) {
                return { customMessage: this.translationService.t("DrivingStyleEditor.UpperRagBoundValidationFail") }
            }

            return null;
        };
    }

    private determineScoreDirection(): void {
        this.highScoreIsBad = false;

        if (!this.metricMapping.defaults) {
            console.warn("No defaults mapped to metric mapping", { metricMapping: this.metricMapping });
            return;
        }

        this.highScoreIsBad = this.metricMapping.defaults.HighScoreValue < this.metricMapping.defaults.LowScoreValue;
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: 'Common.Metric', sorted: true, width: 320 }),
                createCellFunction: mapping => this.vTableService.createTableCell(mapping.label)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'Common.Weighting', sortType: SortType.Number }),
                createCellFunction: mapping => this.vTableService.createTableCell(mapping.data?.metricMetadataItem?.ProportionOfTotalScore, {
                    component: VProgressBar,
                    componentInputs: {
                        percentage: mapping.count,
                        roundNumber: false,
                        grayscale: mapping.data !== this.metricMapping
                    }
                })
            }
        ]
    }
}
